"use client";

import { useEffect } from "react";

// Mounted once in the root layout to start browser RUM tracing on the client.
// Dynamically imported so the browser-only OpenTelemetry SDK is never pulled
// into the server bundle.
//
// The SDK (web tracer + auto-instrumentations + zone context manager) is a few
// hundred KB of JS that patches fetch/XHR/history on import. Starting it during
// hydration put all of that on the critical path and showed up as blocking time
// in Lighthouse, so the import is deferred until the page has loaded and the
// main thread is idle. Tracing a few hundred ms later costs nothing: the
// document-load instrumentation reads the already-recorded PerformanceTimeline,
// so the navigation spans are still complete.
//
// Error reporting must not wait for that, though, so cheap error listeners are
// installed right away and pull the SDK in immediately if something throws
// before the idle callback runs.
export default function OtelBrowser() {
  useEffect(() => {
    let cancelled = false;
    let started = false;
    let idleHandle: number | undefined;

    const onError = (event: ErrorEvent) => load(event.error ?? event.message);
    const onRejection = (event: PromiseRejectionEvent) => load(event.reason);

    // Once the SDK is in, it registers its own error listeners; drop ours so
    // nothing gets reported twice.
    const removeEarlyListeners = () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };

    const load = (pendingError?: unknown) => {
      if (started || cancelled) return;
      started = true;
      removeEarlyListeners();

      void import("@/otel/browser-otel").then((m) => {
        if (cancelled) return;
        m.initBrowserOtel();
        if (pendingError !== undefined) m.reportClientError(pendingError);
      });
    };

    const scheduleLoad = () => {
      if (cancelled) return;
      if (typeof window.requestIdleCallback === "function") {
        idleHandle = window.requestIdleCallback(() => load(), { timeout: 5000 });
      } else {
        idleHandle = window.setTimeout(() => load(), 2000);
      }
    };

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);

    if (document.readyState === "complete") {
      scheduleLoad();
    } else {
      window.addEventListener("load", scheduleLoad, { once: true });
    }

    return () => {
      cancelled = true;
      removeEarlyListeners();
      window.removeEventListener("load", scheduleLoad);
      if (idleHandle === undefined) return;
      if (typeof window.cancelIdleCallback === "function") {
        window.cancelIdleCallback(idleHandle);
      } else {
        window.clearTimeout(idleHandle);
      }
    };
  }, []);

  return null;
}
